import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import { notificationService } from '../services/notificationService';
import { Project } from '../types';

interface CloudIntegrationsProps {
  project: Project;
  onFilesImported: () => void;
  onNavigateToIntegrations?: () => void;
}

interface CloudIntegration {
  id: number;
  provider: string;
  provider_name: string;
  provider_email: string;
  is_active: boolean;
  sync_status: string;
}

interface CloudFile {
  id: string;
  name: string;
  size: number;
  file_type: string;
  path: string;
  created_at?: string;
  modified_at?: string;
}

interface CloudFolder {
  id: string;
  name: string;
  path: string;
}

interface BreadcrumbItem {
  id: string | null;
  name: string;
}

const CloudIntegrations: React.FC<CloudIntegrationsProps> = ({ project, onFilesImported, onNavigateToIntegrations }) => {
  const [integrations, setIntegrations] = useState<CloudIntegration[]>([]);
  const [selectedIntegration, setSelectedIntegration] = useState<CloudIntegration | null>(null);
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [folders, setFolders] = useState<CloudFolder[]>([]);
  const [files, setFiles] = useState<CloudFile[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showBrowser, setShowBrowser] = useState(false);
  const [importMode, setImportMode] = useState<'files' | 'queries'>('files');

  useEffect(() => {
    loadIntegrations();
  }, []);

  const loadIntegrations = async () => {
    try {
      const response = await apiService.getIntegrations();
      setIntegrations(response);
    } catch (error) {
      console.error('Error loading integrations:', error);
      notificationService.error('Failed to load cloud integrations');
    }
  };

  const getProviderDisplayName = (provider: string) => {
    switch (provider) {
      case 'google-drive': return 'Google Drive';
      case 'onedrive': return 'OneDrive';
      case 'box': return 'Box';
      case 'sharepoint': return 'SharePoint';
      default: return provider;
    }
  };

  const handleConnectIntegration = () => {
    if (onNavigateToIntegrations) {
      onNavigateToIntegrations();
    } else {
      notificationService.info('Please go to Integrations page to connect cloud storage');
    }
  };

  // Get list of connected provider types to filter from "Connect" section
  const connectedProviders = new Set(integrations.map(integration => integration.provider));

  const browseIntegration = async (integration: CloudIntegration) => {
    setSelectedIntegration(integration);
    setShowBrowser(true);
    setCurrentFolder(null);
    setBreadcrumbs([{ id: null, name: 'Root' }]);
    await loadFolderContents(integration, null);
  };

  const loadFolderContents = async (integration: CloudIntegration, folderId: string | null, folderName?: string) => {
    setIsLoading(true);
    
    try {
      // Load folders and files based on provider and import mode
      let foldersResponse, filesResponse;
      
      if (integration.provider === 'google-drive') {
        [foldersResponse, filesResponse] = await Promise.all([
          apiService.getIntegrationFolders('google-drive', integration.id, folderId || undefined),
          importMode === 'queries' 
            ? apiService.getIntegrationQueryFiles('google-drive', integration.id, folderId || undefined)
            : apiService.getIntegrationFiles('google-drive', integration.id, folderId || undefined)
        ]);
      } else if (integration.provider === 'onedrive') {
        [foldersResponse, filesResponse] = await Promise.all([
          apiService.getIntegrationFolders('onedrive', integration.id, folderId || undefined),
          importMode === 'queries'
            ? apiService.getIntegrationQueryFiles('onedrive', integration.id, folderId || undefined)
            : apiService.getIntegrationFiles('onedrive', integration.id, folderId || undefined)
        ]);
      } else {
        // Fallback for other providers
        foldersResponse = { folders: [] };
        filesResponse = { files: [] };
      }

      setFolders(foldersResponse.folders || []);
      setFiles(filesResponse.files || []);
      setCurrentFolder(folderId);
      
      // Update breadcrumbs when navigating to a folder
      if (folderId && folderName) {
        setBreadcrumbs(prev => [...prev, { id: folderId, name: folderName }]);
      }
      
    } catch (error) {
      console.error('Error loading folder contents:', error);
      notificationService.error('Failed to load folder contents');
    } finally {
      setIsLoading(false);
    }
  };

  const navigateToBreadcrumb = async (breadcrumbIndex: number) => {
    if (!selectedIntegration) return;
    
    const targetBreadcrumb = breadcrumbs[breadcrumbIndex];
    const newBreadcrumbs = breadcrumbs.slice(0, breadcrumbIndex + 1);
    
    setBreadcrumbs(newBreadcrumbs);
    setCurrentFolder(targetBreadcrumb.id);
    
    await loadFolderContents(selectedIntegration, targetBreadcrumb.id);
  };

  const toggleFileSelection = (fileId: string) => {
    const newSelection = new Set(selectedFiles);
    if (newSelection.has(fileId)) {
      newSelection.delete(fileId);
    } else {
      newSelection.add(fileId);
    }
    setSelectedFiles(newSelection);
  };

  const importSelectedFiles = async () => {
    if (!selectedIntegration || selectedFiles.size === 0) {
      notificationService.warning(`Please select ${importMode} to import`);
      return;
    }

    setIsImporting(true);

    try {
      const fileSelections = files
        .filter(file => selectedFiles.has(file.id))
        .map(file => ({
          id: file.id,
          name: file.name,
          size: file.size,
          file_type: file.file_type,
          path: file.path,
          created_at: file.created_at,
          modified_at: file.modified_at
        }));

      if (importMode === 'queries') {
        await apiService.importQueries(selectedIntegration.id, project.id, fileSelections);
        notificationService.success(`✅ Started importing ${selectedFiles.size} query files`);
      } else {
        await apiService.importFiles(selectedIntegration.id, project.id, fileSelections);
        notificationService.success(`✅ Started importing ${selectedFiles.size} files`);
      }

      setShowBrowser(false);
      setSelectedFiles(new Set());
      onFilesImported();

    } catch (error: any) {
      console.error(`Error importing ${importMode}:`, error);
      notificationService.error(`Failed to import ${importMode}`);
    } finally {
      setIsImporting(false);
    }
  };

  const importFolder = async (folder: CloudFolder) => {
    if (!selectedIntegration) return;

    setIsImporting(true);

    try {
      await apiService.importFolder(selectedIntegration.id, project.id, folder.id, folder.name);
      notificationService.success(`✅ Started importing folder "${folder.name}" and all its contents`);
      setShowBrowser(false);
      onFilesImported();

    } catch (error: any) {
      console.error('Error importing folder:', error);
      notificationService.error('Failed to import folder');
    } finally {
      setIsImporting(false);
    }
  };

  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case 'google-drive': return 'bi-google';
      case 'onedrive': return 'bi-microsoft';
      case 'box': return 'bi-box';
      case 'sharepoint': return 'bi-microsoft';
      default: return 'bi-cloud';
    }
  };

  const getProviderColor = (provider: string) => {
    switch (provider) {
      case 'google-drive': return '#4285f4';
      case 'onedrive': return '#0078d4';
      case 'box': return '#0061d5';
      case 'sharepoint': return '#0078d4';
      default: return '#6366f1';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div style={{ background: 'var(--bg-color)', borderRadius: '0.5rem', padding: '1.5rem' }}>
      {!showBrowser ? (
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem'
          }}>
            <h5 style={{ color: 'var(--text-primary)', margin: 0, fontWeight: 600 }}>Cloud Storage Integrations</h5>
          </div>

          {/* Connected Integrations */}
          {integrations.length > 0 && (
            <div style={{ marginBottom: '2rem' }}>
              <h6 style={{ color: 'var(--text-primary)', fontSize: '0.9rem', marginBottom: '1rem', fontWeight: 600 }}>
                Connected Storage
              </h6>
              <div style={{ display: 'grid', gap: '1rem' }}>
                {integrations.map((integration) => (
                  <div 
                    key={integration.id}
                    style={{
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '0.5rem',
                      padding: '1.25rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--bg-hover)';
                      e.currentTarget.style.borderColor = 'var(--border-hover)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'var(--bg-secondary)';
                      e.currentTarget.style.borderColor = 'var(--border-color)';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <i 
                        className={`bi ${getProviderIcon(integration.provider)}`}
                        style={{ 
                          fontSize: '1.5rem', 
                          color: getProviderColor(integration.provider),
                          marginRight: '1rem' 
                        }}
                      ></i>
                      <div>
                        <div style={{ color: 'var(--text-primary)', fontWeight: 500, fontSize: '0.95rem' }}>
                          {getProviderDisplayName(integration.provider)}
                        </div>
                        <small style={{ color: 'var(--text-secondary)' }}>
                          {integration.provider_email}
                        </small>
                      </div>
                    </div>
                    <button
                      onClick={() => browseIntegration(integration)}
                      style={{
                        background: 'var(--text-primary)',
                        border: 'none',
                        borderRadius: '0.375rem',
                        padding: '0.5rem 1rem',
                        color: 'var(--bg-color)',
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        fontWeight: 500
                      }}
                    >
                      <i className="bi bi-folder" style={{ marginRight: '0.5rem' }}></i>
                      Browse
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Connect New Integrations */}
          <div>
            <h6 style={{ color: 'var(--text-primary)', fontSize: '0.9rem', marginBottom: '1rem', fontWeight: 600 }}>
              Connect Cloud Storage
            </h6>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {!connectedProviders.has('google-drive') && (
                <button
                  onClick={handleConnectIntegration}
                  style={{
                    background: 'var(--bg-color)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0.5rem',
                    padding: '1.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    width: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#4285f4';
                    e.currentTarget.style.background = 'var(--bg-secondary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border-color)';
                    e.currentTarget.style.background = 'var(--bg-color)';
                  }}
                >
                  <i className="bi bi-google" style={{ 
                    fontSize: '1.5rem', 
                    color: '#4285f4', 
                    marginRight: '1rem' 
                  }}></i>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ color: 'var(--text-primary)', fontWeight: 500, fontSize: '0.95rem' }}>Google Drive</div>
                    <small style={{ color: 'var(--text-secondary)' }}>Connect your Google Drive account</small>
                  </div>
                </button>
              )}

              {!connectedProviders.has('onedrive') && (
                <button
                  onClick={handleConnectIntegration}
                  style={{
                    background: 'var(--bg-color)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0.5rem',
                    padding: '1.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    width: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#0078d4';
                    e.currentTarget.style.background = 'var(--bg-secondary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border-color)';
                    e.currentTarget.style.background = 'var(--bg-color)';
                  }}
                >
                  <i className="bi bi-microsoft" style={{ 
                    fontSize: '1.5rem', 
                    color: '#0078d4', 
                    marginRight: '1rem' 
                  }}></i>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ color: 'var(--text-primary)', fontWeight: 500, fontSize: '0.95rem' }}>OneDrive</div>
                    <small style={{ color: 'var(--text-secondary)' }}>Connect your OneDrive account</small>
                  </div>
                </button>
              )}

              {!connectedProviders.has('box') && (
                <div
                  style={{
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0.5rem',
                    padding: '1.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    opacity: 0.6
                  }}
                >
                  <i className="bi bi-box" style={{ 
                    fontSize: '1.5rem', 
                    color: '#0061d5', 
                    marginRight: '1rem' 
                  }}></i>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ color: 'var(--text-secondary)', fontWeight: 500, fontSize: '0.95rem' }}>Box</div>
                    <small style={{ color: 'var(--text-muted)' }}>Coming soon</small>
                  </div>
                </div>
              )}

              {!connectedProviders.has('sharepoint') && (
                <div
                  style={{
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '0.5rem',
                    padding: '1.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    opacity: 0.6
                  }}
                >
                  <i className="bi bi-microsoft" style={{ 
                    fontSize: '1.5rem', 
                    color: '#0078d4', 
                    marginRight: '1rem' 
                  }}></i>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ color: 'var(--text-secondary)', fontWeight: 500, fontSize: '0.95rem' }}>SharePoint</div>
                    <small style={{ color: 'var(--text-muted)' }}>Coming soon</small>
                  </div>
                </div>
              )}

              {/* Show message if all providers are connected */}
              {connectedProviders.size > 0 && 
               connectedProviders.has('google-drive') && 
               connectedProviders.has('onedrive') && (
                <div style={{
                  textAlign: 'center',
                  padding: '2rem',
                  color: 'var(--text-secondary)',
                  fontSize: '0.9rem'
                }}>
                  <i className="bi bi-check-circle" style={{
                    fontSize: '2rem',
                    color: 'var(--success-color)',
                    marginBottom: '0.5rem',
                    display: 'block'
                  }}></i>
                  All available cloud storage providers are connected.
                  <br />
                  <small style={{ color: 'var(--text-muted)' }}>
                    More providers coming soon.
                  </small>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div>
          {/* File Browser */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '1.5rem' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <button
                onClick={() => setShowBrowser(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  marginRight: '0.75rem',
                  fontSize: '1.25rem'
                }}
              >
                <i className="bi bi-arrow-left"></i>
              </button>
              <h5 style={{ color: 'var(--text-primary)', margin: 0, fontWeight: 600 }}>
                {selectedIntegration?.provider_name}
              </h5>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={importSelectedFiles}
                disabled={selectedFiles.size === 0 || isImporting}
                style={{
                  background: selectedFiles.size > 0 ? 'var(--text-primary)' : 'var(--border-color)',
                  border: 'none',
                  borderRadius: '0.375rem',
                  padding: '0.5rem 1rem',
                  color: selectedFiles.size > 0 ? 'var(--bg-color)' : 'var(--text-muted)',
                  fontSize: '0.85rem',
                  cursor: selectedFiles.size > 0 ? 'pointer' : 'not-allowed',
                  fontWeight: 500
                }}
              >
                {isImporting ? (
                  <>
                    <div className="spinner-border spinner-border-sm" style={{ marginRight: '0.5rem' }}></div>
                    Importing...
                  </>
                ) : (
                  <>
                    <i className="bi bi-download" style={{ marginRight: '0.5rem' }}></i>
                    Import ({selectedFiles.size})
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Import Mode Toggle */}
          <div style={{
            marginBottom: '1.5rem',
            display: 'flex',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: '0.375rem',
            padding: '0.25rem'
          }}>
            <button
              onClick={() => {
                setImportMode('files');
                setSelectedFiles(new Set());
                if (selectedIntegration) {
                  loadFolderContents(selectedIntegration, currentFolder);
                }
              }}
              style={{
                flex: 1,
                background: importMode === 'files' ? 'var(--text-primary)' : 'transparent',
                border: 'none',
                borderRadius: '0.25rem',
                padding: '0.5rem',
                color: importMode === 'files' ? 'var(--bg-color)' : 'var(--text-secondary)',
                fontSize: '0.85rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontWeight: 500
              }}
            >
              <i className="bi bi-file-earmark" style={{ marginRight: '0.5rem' }}></i>
              Documents
            </button>
            <button
              onClick={() => {
                setImportMode('queries');
                setSelectedFiles(new Set());
                if (selectedIntegration) {
                  loadFolderContents(selectedIntegration, currentFolder);
                }
              }}
              style={{
                flex: 1,
                background: importMode === 'queries' ? 'var(--text-primary)' : 'transparent',
                border: 'none',
                borderRadius: '0.25rem',
                padding: '0.5rem',
                color: importMode === 'queries' ? 'var(--bg-color)' : 'var(--text-secondary)',
                fontSize: '0.85rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontWeight: 500
              }}
            >
              <i className="bi bi-search" style={{ marginRight: '0.5rem' }}></i>
              Queries
            </button>
          </div>

          {/* Breadcrumb Navigation */}
          {breadcrumbs.length > 0 && (
            <div style={{
              marginBottom: '1.5rem',
              padding: '0.75rem',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '0.375rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                {breadcrumbs.map((breadcrumb, index) => (
                  <React.Fragment key={breadcrumb.id || 'root'}>
                    <button
                      onClick={() => navigateToBreadcrumb(index)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: index === breadcrumbs.length - 1 ? 'var(--text-primary)' : 'var(--text-secondary)',
                        cursor: index === breadcrumbs.length - 1 ? 'default' : 'pointer',
                        fontSize: '0.85rem',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.25rem',
                        textDecoration: index === breadcrumbs.length - 1 ? 'none' : 'underline',
                        fontWeight: index === breadcrumbs.length - 1 ? 600 : 500
                      }}
                      onMouseEnter={(e) => {
                        if (index !== breadcrumbs.length - 1) {
                          e.currentTarget.style.background = 'var(--bg-hover)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'none';
                      }}
                    >
                      {breadcrumb.name}
                    </button>
                    {index < breadcrumbs.length - 1 && (
                      <i className="bi bi-chevron-right" style={{
                        color: 'var(--text-muted)',
                        fontSize: '0.75rem',
                        margin: '0 0.5rem'
                      }}></i>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '3rem 0' }}>
              <div className="spinner-border" style={{ color: 'var(--primary-color)' }}></div>
              <p style={{ color: 'var(--text-secondary)', marginTop: '1rem' }}>Loading files...</p>
            </div>
          ) : (
            <div>
              {/* Folders */}
              {folders.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h6 style={{ color: 'var(--text-primary)', fontSize: '0.9rem', marginBottom: '0.75rem', fontWeight: 600 }}>
                    Folders
                  </h6>
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    {folders.map((folder) => (
                      <div
                        key={folder.id}
                        style={{
                          background: 'var(--bg-secondary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '0.375rem',
                          padding: '1rem',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <div 
                          onClick={() => loadFolderContents(selectedIntegration!, folder.id, folder.name)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            flex: 1
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'var(--bg-hover)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                          }}
                        >
                          <i className="bi bi-folder" style={{ 
                            color: '#f59e0b', 
                            marginRight: '0.75rem',
                            fontSize: '1.25rem'
                          }}></i>
                          <span style={{ color: 'var(--text-primary)', fontSize: '0.95rem', fontWeight: 500 }}>
                            {folder.name}
                          </span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            importFolder(folder);
                          }}
                          disabled={isImporting}
                          style={{
                            background: 'var(--success-color)',
                            border: 'none',
                            borderRadius: '0.25rem',
                            padding: '0.375rem 0.75rem',
                            color: 'white',
                            fontSize: '0.8rem',
                            cursor: isImporting ? 'not-allowed' : 'pointer',
                            opacity: isImporting ? 0.6 : 1,
                            fontWeight: 500
                          }}
                        >
                          <i className="bi bi-download" style={{ marginRight: '0.25rem' }}></i>
                          Import All
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Files */}
              {files.length > 0 && (
                <div>
                  <h6 style={{ color: 'var(--text-primary)', fontSize: '0.9rem', marginBottom: '0.75rem', fontWeight: 600 }}>
                    Files ({files.length})
                  </h6>
                  <div style={{ display: 'grid', gap: '0.75rem', maxHeight: '300px', overflowY: 'auto' }}>
                    {files.map((file) => (
                      <div
                        key={file.id}
                        onClick={() => toggleFileSelection(file.id)}
                        style={{
                          background: selectedFiles.has(file.id) ? 'var(--info-bg)' : 'var(--bg-secondary)',
                          border: selectedFiles.has(file.id) ? '1px solid var(--info-color)' : '1px solid var(--border-color)',
                          borderRadius: '0.375rem',
                          padding: '1rem',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!selectedFiles.has(file.id)) {
                            e.currentTarget.style.background = 'var(--bg-hover)';
                            e.currentTarget.style.borderColor = 'var(--border-hover)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!selectedFiles.has(file.id)) {
                            e.currentTarget.style.background = 'var(--bg-secondary)';
                            e.currentTarget.style.borderColor = 'var(--border-color)';
                          }
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selectedFiles.has(file.id)}
                            onChange={() => {}}
                            style={{ marginRight: '0.75rem' }}
                          />
                          <i className={importMode === 'queries' ? 'bi bi-code-square' : `bi bi-file-earmark-${file.file_type}`} style={{
                            color: 'var(--primary-color)',
                            marginRight: '0.75rem',
                            fontSize: '1.25rem'
                          }}></i>
                          <div>
                            <div style={{ color: 'var(--text-primary)', fontSize: '0.95rem', fontWeight: 500 }}>
                              {file.name}
                            </div>
                            <small style={{ color: 'var(--text-secondary)' }}>
                              {formatFileSize(file.size)} • {file.file_type.toUpperCase()}
                            </small>
                          </div>
                        </div>
                        {selectedFiles.has(file.id) && (
                          <i className="bi bi-check-circle" style={{ color: 'var(--success-color)', fontSize: '1.25rem' }}></i>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {files.length === 0 && folders.length === 0 && (
                <div style={{ textAlign: 'center', padding: '3rem 0' }}>
                  <i className={importMode === 'queries' ? 'bi bi-search' : 'bi bi-folder-x'} style={{ fontSize: '2.5rem', color: 'var(--text-muted)' }}></i>
                  <p style={{ color: 'var(--text-secondary)', marginTop: '1rem', fontSize: '1rem' }}>
                    {importMode === 'queries' ? 'No query files found' : 'No supported files found'}
                  </p>
                  <small style={{ color: 'var(--text-muted)' }}>
                    {importMode === 'queries'
                      ? 'Only JSON, CSV, and TXT files are supported for query import'
                      : 'Only PDF, DOCX, and PPTX files are supported'
                    }
                  </small>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CloudIntegrations;
